import {
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    HStack,
    IconButton,
    Text,
    Tooltip,
    VStack,
    useClipboard,
    useToast,
} from '@chakra-ui/react';
import React from 'react';
import { FaCopy } from 'react-icons/fa';
import { translate } from '../../../i18n';
import { UserDto } from '../../../models/user/dto/user.dto';
import { formatCoveredAmount } from './paid-by.utils';

interface PaidByDetailsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    payer: UserDto;
    coveredUser: UserDto;
    paymentIntentId: string;
    amountCents: number;
    /** Currency of the PaymentIntent that charged the share (EUR when omitted). */
    currency?: string;
}

/**
 * Side panel surfacing the pay-for-others batch context for a single covered
 * player: who paid and how much, with a copyable Stripe PaymentIntent ID.
 *
 * Read-only by design. It used to carry a "refund this player's share" button
 * wired to `POST payments/admin/:pi/refund-covered/:userId` — an endpoint the
 * API has never implemented (the client half of a split change landed alone in
 * f5649d16f; the server half never did). Every click 404'd into an error toast,
 * so the button was removed rather than left as an affordance the API rejects.
 * The `Refund` entity already carries `coveredUser`/`amountCents`, so the
 * server side remains buildable — tracked as a proposed build in
 * docs/automation/coach-dashboard-hardening-status.md.
 */
const PaidByDetailsDrawer: React.FC<PaidByDetailsDrawerProps> = ({
    isOpen,
    onClose,
    payer,
    coveredUser,
    paymentIntentId,
    amountCents,
    currency,
}) => {
    const toast = useToast();
    const { hasCopied, onCopy } = useClipboard(paymentIntentId);

    const handleCopy = () => {
        onCopy();
        toast({
            title: translate('common.copied'),
            status: 'info',
            duration: 1500,
            isClosable: true,
        });
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader>{translate('game.paid_by_details_title')}</DrawerHeader>
                <DrawerBody>
                    <VStack align="stretch" spacing={5}>
                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                {translate('game.paid_by')}
                            </Text>
                            <Text fontWeight="semibold">
                                {payer.firstName} {payer.lastName}
                            </Text>
                            {payer.email && (
                                <Text fontSize="sm" color="gray.500">
                                    {payer.email}
                                </Text>
                            )}
                        </VStack>

                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                {translate('game.covered_player')}
                            </Text>
                            <Text fontWeight="semibold">
                                {coveredUser.firstName} {coveredUser.lastName}
                            </Text>
                            {coveredUser.email && (
                                <Text fontSize="sm" color="gray.500">
                                    {coveredUser.email}
                                </Text>
                            )}
                        </VStack>

                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                {translate('game.covered_share_amount')}
                            </Text>
                            <Text fontWeight="semibold">
                                {formatCoveredAmount(amountCents, currency)}
                            </Text>
                        </VStack>

                        <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                {translate('game.stripe_payment_intent_id')}
                            </Text>
                            <HStack>
                                <Text
                                    fontFamily="mono"
                                    fontSize="sm"
                                    wordBreak="break-all"
                                >
                                    {paymentIntentId}
                                </Text>
                                <Tooltip
                                    label={
                                        hasCopied
                                            ? translate('common.copied')
                                            : translate('common.copy')
                                    }
                                >
                                    <IconButton
                                        aria-label={translate('common.copy')}
                                        icon={<FaCopy />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={handleCopy}
                                    />
                                </Tooltip>
                            </HStack>
                        </VStack>
                    </VStack>
                </DrawerBody>
                <DrawerFooter>
                    <Button variant="outline" onClick={onClose}>
                        {translate('common.close')}
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default PaidByDetailsDrawer;
